import React from "react";

/**
 * Logo — pure SVG mark for Abhinav Dhiman / The Digital Marketer.
 * A brutalist "A" with a red signal crossbar. No external image dependencies.
 *
 * variants:
 *   "dark"  — ink square, paper A, red crossbar (default, for use on light surfaces)
 *   "light" — no fill background, ink A, red crossbar (for use on dark surfaces)
 *   "mark"  — just the mark, transparent (for large hero use)
 */
export default function Logo({ variant = "dark", size = 36, className = "" }) {
  const V = 48;

  if (variant === "dark") {
    return (
      <span
        className={`inline-flex items-center justify-center bg-ink ${className}`}
        style={{ width: size, height: size }}
        aria-label="The Digital Marketer logo"
        data-testid="site-logo"
      >
        <svg
          viewBox={`0 0 ${V} ${V}`}
          width={size * 0.72}
          height={size * 0.72}
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          {/* A silhouette with inner counter — even-odd for cutout */}
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M24 5 L46 43 L36 43 L33 37 L15 37 L12 43 L2 43 Z M20 29 L28 29 L24 20 Z"
            fill="#FDFDFD"
          />
          {/* Signal crossbar */}
          <rect x="14" y="27" width="20" height="4" fill="#E63946" />
        </svg>
      </span>
    );
  }

  if (variant === "light") {
    return (
      <span
        className={`inline-flex items-center justify-center ${className}`}
        style={{ width: size, height: size }}
        aria-label="The Digital Marketer logo"
        data-testid="site-logo"
      >
        <svg
          viewBox={`0 0 ${V} ${V}`}
          width={size}
          height={size}
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M24 5 L46 43 L36 43 L33 37 L15 37 L12 43 L2 43 Z M20 29 L28 29 L24 20 Z"
            fill="#0A0A0A"
          />
          <rect x="14" y="27" width="20" height="4" fill="#E63946" />
        </svg>
      </span>
    );
  }

  // "mark" — raw, unwrapped
  return (
    <svg
      viewBox={`0 0 ${V} ${V}`}
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="The Digital Marketer logo"
      data-testid="site-logo"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M24 5 L46 43 L36 43 L33 37 L15 37 L12 43 L2 43 Z M20 29 L28 29 L24 20 Z"
        fill="currentColor"
      />
      <rect x="14" y="27" width="20" height="4" fill="#E63946" />
    </svg>
  );
}
